import React, { useEffect, useState, useCallback } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog'
import { Input } from '../ui/input'
import { Button } from '../ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select'
import { FormError } from '../ui/FormError'
import { Spinner } from '@/app/lib/loader/loader'
import { useForm, useFieldArray, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import z from 'zod'
import { toast } from 'sonner'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { Image as ImageIcon, Check, Plus, AlertCircle } from 'lucide-react'
import { bannerService, StoreBanner, BannerType } from '../../services/banner.service'
import { SortableSlideItem } from './SortableSlideItem'
import { useQuery, useQueryClient } from '@tanstack/react-query'

const slideSchema = z.object({
  id: z.number().optional(),
  banner_id: z.number().optional(),
  image_url: z.string().optional(),
  call_to_action_url: z.string().url('URL no válida').optional().or(z.literal('')),
  sort_order: z.number(),
  is_active: z.boolean(),
  file: z.any().optional(),
  preview_url: z.string().optional(),
}).refine(data => data.image_url || data.file, {
  message: "La imagen es requerida",
  path: ["image_url"]
})

const bannerSchema = z.object({
  name: z.string().min(1, 'El nombre es requerido'),
  type: z.enum(['HERO', 'BANNER_GRID_3', 'SINGLE_BANNER']),  // TIPOS ACEPTADOS
  is_active: z.boolean(),
  starts_at: z.string().optional().nullable(),
  ends_at: z.string().optional().nullable(),
  slides: z.array(slideSchema)
}).superRefine((data, ctx) => {
  if (data.starts_at && data.ends_at) {
    if (new Date(data.starts_at) > new Date(data.ends_at)) {
      ctx.addIssue({
        code: 'custom',
        message: 'La fecha de inicio no puede ser posterior a la de fin',
        path: ['starts_at'],
      })
    }
  }
  if (data.type === 'BANNER_GRID_3' && data.slides.length !== 3) {
    ctx.addIssue({
      code: 'custom',
      message: 'El tipo GRID requiere exactamente 3 imágenes',
      path: ['slides'],
    })
  }
  if (data.type === 'HERO' && data.slides.length === 0) {
    ctx.addIssue({
      code: 'custom',
      message: 'Debe agregar al menos un slide',
      path: ['slides'],
    })
  }
  if (data.type === 'SINGLE_BANNER' && data.slides.length !== 1) {
    ctx.addIssue({
      code: 'custom',
      message: 'El tipo SINGLE_BANNER requiere exactamente 1 slide',
      path: ['slides'],
    })
  }
})

type BannerFormValues = z.infer<typeof bannerSchema>

interface BannerModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  bannerId: number | null
  onSuccess: () => void
}

export function BannerModal({ open, onOpenChange, bannerId, onSuccess }: BannerModalProps) {
  const isEditing = !!bannerId
  const queryClient = useQueryClient()
  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<BannerFormValues>({
    resolver: zodResolver(bannerSchema),
    defaultValues: {
      name: '',
      type: 'HERO',
      is_active: true,
      starts_at: '',
      ends_at: '',
      slides: [
        { sort_order: 1, is_active: true }
      ]
    }
  })

  const { fields: slides, append, remove, move } = useFieldArray({
    control,
    name: 'slides',
  })

  const bannerType = watch('type')
  const isActiveValue = watch('is_active')

  const { data: banner, isLoading } = useQuery({
    queryKey: ['banner', bannerId],
    queryFn: () => bannerService.getBanner(bannerId!),
    enabled: !!bannerId && open,
  })

  useEffect(() => {
    if (banner && open) {
      reset({
        name: banner.name,
        type: banner.type,
        is_active: banner.is_active,
        starts_at: banner.starts_at ? banner.starts_at.split('T')[0] : '',
        ends_at: banner.ends_at ? banner.ends_at.split('T')[0] : '',
        slides: banner.slides.map(s => ({
          ...s,
          call_to_action_url: s.call_to_action_url || '',
          preview_url: s.image_url
        }))
      })
    }
  }, [banner, open, reset])

  // MANEJO de cambio de tipo
  useEffect(() => {
    if (!open) return

    const currentSlides = getValues('slides')

    if (bannerType === 'BANNER_GRID_3') {
      const newSlides = Array.from({ length: 3 }, (_, index) => ({
        ...(currentSlides[index] || {}),
        sort_order: index + 1,
        is_active: currentSlides[index]?.is_active ?? true,
      }))

      setValue('slides', newSlides, {
        shouldValidate: true,
        shouldDirty: true,
      })
    }

    if (bannerType === 'SINGLE_BANNER') {
      const newSlides = [
        {
          ...(currentSlides[0] || {}),
          sort_order: 1,
          is_active: currentSlides[0]?.is_active ?? true,
        },
      ]

      setValue('slides', newSlides, {
        shouldValidate: true,
        shouldDirty: true,
      })
    }

    if (bannerType === 'HERO' && currentSlides.length === 0) {
      setValue(
        'slides',
        [
          {
            sort_order: 1,
            is_active: true,
          },
        ],
        {
          shouldValidate: true,
          shouldDirty: true,
        }
      )
    }
  }, [bannerType, open, getValues, setValue])

  const handleAddSlide = () => {
    append({
      sort_order: slides.length + 1,
      is_active: true,
      call_to_action_url: ''
    })
  }

  const handleFileChange = (index: number, file: File) => {
    const previewUrl = URL.createObjectURL(file)
    setValue(`slides.${index}.file`, file)
    setValue(`slides.${index}.preview_url`, previewUrl)
    setValue(`slides.${index}.image_url`, undefined)
  }

  const moveSlide = useCallback((dragIndex: number, hoverIndex: number) => {
    move(dragIndex, hoverIndex)
  }, [move])

  const onSubmit = async (data: BannerFormValues) => {
    try {
      const formData = new FormData()
      formData.append('name', data.name)
      formData.append('type', data.type)
      formData.append('is_active', data.is_active ? '1' : '0')
      if (data.starts_at) formData.append('starts_at', data.starts_at)
      if (data.ends_at) formData.append('ends_at', data.ends_at)

      data.slides.forEach((slide, index) => {
        const order = index + 1
        formData.append(`slides[${index}][sort_order]`, String(order))
        formData.append(`slides[${index}][is_active]`, slide.is_active ? '1' : '0')

        if (slide.id) formData.append(`slides[${index}][id]`, String(slide.id))
        if (slide.call_to_action_url) formData.append(`slides[${index}][call_to_action_url]`, slide.call_to_action_url)

        if (slide.file) {
          formData.append(`slides[${index}][file]`, slide.file)
        } else if (slide.image_url) {
          formData.append(`slides[${index}][image_url]`, slide.image_url)
        }
      })

      if (isEditing && bannerId) {
        await bannerService.updateBanner(bannerId, formData)
        queryClient.invalidateQueries({ queryKey: ['banner', bannerId] })
        toast.success('Banner actualizado exitosamente')
      } else {
        await bannerService.createBanner(formData)
        toast.success('Banner creado exitosamente')
      }
      onSuccess()
    } catch (error) {
      toast.error('Ocurrió un error al guardar el banner')
      console.error(error)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg font-semibold text-card-foreground">
              <ImageIcon className="h-5 w-5 text-primary" />
              {isEditing ? 'Editar Banner' : 'Nuevo Banner'}
            </DialogTitle>
            <DialogDescription className="text-card-description">
              {isEditing
                ? 'Modifica los datos del banner y sus imágenes.'
                : 'Configura un nuevo banner para la tienda en línea.'}
            </DialogDescription>
          </DialogHeader>

          {isLoading ? (
            <div className="flex justify-center py-8">
              <Spinner className="h-8 w-8" />
            </div>
          ) : (
            <div className="space-y-6 py-4">
              {/* Información General */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-card-foreground">
                    Nombre <span className="text-red-500">*</span>
                  </label>
                  <Input placeholder="Ej. Promociones Verano" {...register('name')} />
                  <FormError message={errors.name?.message} />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-card-foreground">
                    Tipo de Banner <span className="text-red-500">*</span>
                  </label>
                  <Select
                    value={bannerType}
                    onValueChange={(value) => setValue('type', value as BannerType)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecciona el tipo de banner" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="HERO">Hero (Carrusel múltiple)</SelectItem>
                      <SelectItem value="BANNER_GRID_3">Banner Grid (3 espacios fijos)</SelectItem>
                      <SelectItem value="SINGLE_BANNER">Banner Individual (1 espacio)</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormError message={errors.type?.message} />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-card-foreground">
                    Fecha de Inicio (Opcional)
                  </label>
                  <Input type="date" {...register('starts_at')} />
                  <FormError message={errors.starts_at?.message} />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-card-foreground">
                    Fecha de Fin (Opcional)
                  </label>
                  <Input type="date" {...register('ends_at')} />
                  <FormError message={errors.ends_at?.message} />
                </div>
              </div>

              <div className="flex items-center gap-2 pt-2">
                <button
                  type="button"
                  role="switch"
                  aria-checked={isActiveValue}
                  onClick={() => setValue('is_active', !isActiveValue)}
                  className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full transition-colors duration-200 ease-in-out focus:outline-none ${isActiveValue ? 'bg-primary' : 'bg-slate-200'
                    }`}
                >
                  <span
                    aria-hidden="true"
                    className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${isActiveValue ? 'translate-x-2' : '-translate-x-2'
                      }`}
                  />
                </button>
                <span className="text-sm font-medium text-card-foreground">Banner Activo</span>
              </div>

              {bannerType === 'BANNER_GRID_3' && (
                <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 p-3">
                  <p className="mb-2 text-xs font-medium text-slate-500">Distribución visual</p>
                  <div className="flex gap-1.5" style={{ height: '80px' }}>
                    {/* Espacio 1: mitad izquierda completa */}
                    <div className="flex flex-1 items-center justify-center rounded bg-slate-200 text-xs font-medium text-slate-500">
                      Espacio 1
                    </div>
                    {/* Mitad derecha dividida verticalmente */}
                    <div className="flex flex-1 flex-col gap-1.5">
                      {/* Espacio 2: ~75% de la altura */}
                      <div
                        className="flex items-center justify-center rounded bg-slate-200 text-xs font-medium text-slate-500"
                        style={{ flex: '3' }}
                      >
                        Espacio 2
                      </div>
                      {/* Espacio 3: ~25% de la altura */}
                      <div
                        className="flex items-center justify-center rounded bg-slate-200 text-xs font-medium text-slate-500"
                        style={{ flex: '1' }}
                      >
                        Espacio 3
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <hr />

              {/* SLIDES */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-medium">Imágenes (Slides)</h3>
                    {bannerType === 'BANNER_GRID_3' ? (
                      <p className="text-sm text-slate-500">Este tipo de banner requiere exactamente 3 imágenes.</p>
                    ) : (
                      <p className="text-sm text-slate-500">Arrastra para reordenar las imágenes.</p>
                    )}
                  </div>
                  {bannerType === 'HERO' && (
                    <Button type="button" onClick={handleAddSlide} variant="outline" size="sm">
                      <Plus className="h-4 w-4 mr-2" /> Agregar imagen
                    </Button>
                  )}
                </div>

                {errors.slides?.root && (
                  <div className="flex items-center gap-2 text-red-500 text-sm bg-red-50 p-3 rounded-md">
                    <AlertCircle size={16} />
                    {errors.slides.root.message}
                  </div>
                )}

                <DndProvider backend={HTML5Backend}>
                  <div className="space-y-3">
                    {slides.map((field, index) => (
                      <SortableSlideItem
                        key={field.id}
                        index={index}
                        slide={watch(`slides.${index}`)}
                        moveSlide={moveSlide}
                        removeSlide={bannerType === 'HERO' ? () => remove(index) : undefined}
                        onFileChange={(file) => handleFileChange(index, file)}
                        register={register}
                        errors={errors}
                        title={bannerType === 'BANNER_GRID_3' ? `Espacio ${index + 1}` : `Slide ${index + 1}`}
                      />
                    ))}
                  </div>
                </DndProvider>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 sm:justify-end mt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting} className="bg-primary hover:bg-primary/90">
              {isSubmitting ? (
                <Spinner className="mr-2 h-4 w-4" />
              ) : (
                <Check className="mr-2 h-4 w-4" />
              )}
              Guardar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
